import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { runCheckout } from "../lib/payments";
import { useAuth } from "../context/AuthContext";
import { Users, Plus, ArrowRight, Loader2, Sparkles } from "lucide-react";

export default function Teams() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [teams, setTeams] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!user) { navigate("/start"); return; }
    api.listTeams().then(setTeams).catch(() => setTeams([]));
    api.pricing(user.country_code).then(setPricing).catch(() => {});
  }, [user, navigate]);

  const create = async () => {
    if (!name.trim()) { toast.error("Give your team a name"); return; }
    setCreating(true);
    try {
      let team;
      try {
        team = await api.createTeam(name.trim());
      } catch (err) {
        if (err?.response?.status === 402 && err?.response?.data?.detail === "team_pass_required") {
          toast.info("A one-time Team Pass unlocks your team");
          const { order } = await api.checkout("team");
          const payload = await runCheckout(order, user);
          const { user: updated } = await api.confirm(order.order_id, payload);
          login({ ...updated, token: user.token });
          team = await api.createTeam(name.trim());
        } else { throw err; }
      }
      toast.success("Team created!");
      navigate(`/team/${team.id}`);
    } catch (err) {
      if (err?.message === "cancelled") { toast.info("Payment cancelled"); }
      else {
        const d = err?.response?.data?.detail;
        if (err?.response?.status === 400) { toast.error(d); navigate("/dashboard"); }
        else if (err?.response?.status === 402) { toast.error("Subscribe first"); navigate("/checkout"); }
        else toast.error(d || "Could not create team");
      }
    } finally { setCreating(false); }
  };

  if (teams === null) return <div className="max-w-3xl mx-auto px-5 py-24 text-center text-[#918780]">Loading…</div>;

  const field = "flex-1 px-4 py-3 rounded-xl border border-[#E8E1D9] bg-white focus:outline-none focus:border-[#6B21A8] focus:ring-2 focus:ring-[#6B21A8]/15 transition";

  return (
    <div className="max-w-3xl mx-auto px-5 py-12 space-y-8">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-center gap-2 text-[#6B21A8] font-semibold mb-1"><Users size={18} /> Team snapshots</div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#2B2625]">Bring your team together</h1>
        <p className="text-[#6E655F] mt-1">Invite your people and get one combined view of how you work best together.</p>
      </motion.div>

      <div className="bg-white border border-[#E8E1D9] rounded-2xl p-6 shadow-sm">
        <label className="text-sm font-semibold text-[#2B2625] block mb-2">Create a new team</label>
        <div className="flex flex-col sm:flex-row gap-3">
          <input data-testid="team-name-input" className={field} value={name}
                 onChange={(e) => setName(e.target.value)} placeholder="e.g. Product Squad" />
          <button data-testid="team-create-btn" onClick={create} disabled={creating}
            className="inline-flex items-center justify-center gap-2 bg-[#6B21A8] hover:bg-[#4C1D95] text-white font-semibold py-3 px-6 rounded-xl transition-colors active:scale-[0.98] disabled:opacity-60">
            {creating ? <Loader2 className="animate-spin" size={18} /> : <><Plus size={18} /> Create</>}
          </button>
        </div>
        {pricing && (
          <p className="text-sm text-[#918780] mt-3">
            One-time Team Pass <b className="text-[#6B21A8]" data-testid="team-pass-price">{pricing.team_display}</b> — covers a team of up to 5 (you + 4). Bigger team? You can request more from inside the team.
          </p>
        )}
      </div>

      {teams.length > 0 && (
        <div className="space-y-3">
          {teams.map((t) => (
            <Link key={t.id} to={`/team/${t.id}`} data-testid={`team-card-${t.id}`}
              className="block bg-white border border-[#E8E1D9] rounded-xl p-5 flex items-center justify-between hover:border-[#6B21A8]/40 transition-colors">
              <div>
                <p className="font-semibold text-[#2B2625] text-lg">{t.name}</p>
                <p className="text-sm text-[#918780]">{t.member_count} member{t.member_count !== 1 ? "s" : ""} · {t.report_status === "ready" ? "Snapshot ready" : "Building"}</p>
              </div>
              <div className="flex items-center gap-3">
                {t.synergy != null && <span className="font-display text-2xl font-bold text-[#3F3A44]">{t.synergy}%</span>}
                <ArrowRight className="text-[#6B21A8]" size={20} />
              </div>
            </Link>
          ))}
        </div>
      )}

      {teams.length === 0 && (
        <div className="text-center text-[#918780] py-8 flex flex-col items-center gap-2">
          <Sparkles size={22} className="text-[#7C3AED]" />
          <p>No teams yet. Create one above to get started.</p>
        </div>
      )}
    </div>
  );
}
